import { useEffect, useRef } from 'react';
import { supabase }                from '@/shared/lib/supabase';
import { listarGruposDeVotacao }   from '../../domain/usecases/selecionarCandidatos';
import { votacaoRepository }       from '../../data/votacaoRepository';
import type { FutebolJogoDia }     from '@/shared/types';

const ANTECEDENCIA_MS      = 30 * 60 * 1000;
const ANTECIPACAO_FIM_MS   =  5 * 60 * 1000;
const RETRY_BACKOFF_MS     = [3_000, 10_000, 30_000, 60_000];
const RETRY_PERSISTENTE_MS = 2 * 60 * 1000;

type ScheduleFn = (fn: () => void, delay: number, geracaoCapturada: number) => void;

// ── Ativação direta (único jogo) com retry ────────────────────────────────────

function ativarJogoComRetry(
  fixtureId: number,
  telaId: string,
  schedule: ScheduleFn,
  geracaoCapturada: number,
): void {
  const tentativas = [...RETRY_BACKOFF_MS];

  const tentar = async () => {
    try {
      console.log('[VotacaoAuto] ativando jogo único sem votação', fixtureId);
      await votacaoRepository.ativarJogoSemVotacao(fixtureId, telaId);
      console.log('[VotacaoAuto] jogo único ativado com sucesso', fixtureId);
    } catch (err) {
      console.error('[VotacaoAuto] erro ao ativar jogo único', fixtureId, err);
      const proximo = tentativas.shift();
      schedule(tentar, proximo ?? RETRY_PERSISTENTE_MS, geracaoCapturada);
    }
  };

  tentar();
}

// ── Encerramento com retry persistente por ciclo ──────────────────────────────

function encerrarCicloComRetry(
  fixtureIds: number[],
  telaId: string,
  schedule: ScheduleFn,
  geracaoCapturada: number,
): void {
  const tentativas = [...RETRY_BACKOFF_MS];

  const tentar = async () => {
    try {
      console.log('[VotacaoAuto] encerrando ciclo', fixtureIds);
      const resultado = await votacaoRepository.encerrarVotacaoCiclo(fixtureIds, telaId);
      if (resultado === null) throw new Error('sem candidatos no banco — iniciarVotacaoCiclo pode ter falhado');
      console.log('[VotacaoAuto] ciclo encerrado com sucesso, vencedor fixture_id', resultado);
    } catch (err) {
      console.error('[VotacaoAuto] erro ao encerrar ciclo', fixtureIds, err);
      const proximo = tentativas.shift();
      schedule(tentar, proximo ?? RETRY_PERSISTENTE_MS, geracaoCapturada);
    }
  };

  tentar();
}

// ── Agendamento de um grupo (ciclo) ──────────────────────────────────────────

function agendarGrupo(
  grupo: FutebolJogoDia[],
  telaId: string,
  schedule: ScheduleFn,
  geracaoCapturada: number,
): void {
  const fixtureIds = grupo.map((j) => j.api_fixture_id);
  const primeiroMs = Math.min(...grupo.map((j) => new Date(j.horario).getTime()));
  const inicioMs   = primeiroMs - ANTECEDENCIA_MS;
  const fimMs      = primeiroMs - ANTECIPACAO_FIM_MS;
  const now        = Date.now();

  const label = `fixtures=${fixtureIds.join(',')} jogo=${new Date(primeiroMs).toLocaleTimeString('pt-BR')}`;
  console.log(`[VotacaoAuto] agendarGrupo ${label} | abre=${new Date(inicioMs).toLocaleTimeString('pt-BR')} fecha=${new Date(fimMs).toLocaleTimeString('pt-BR')} agora=${new Date(now).toLocaleTimeString('pt-BR')}`);

  // Único jogo: não abre votação — ativa diretamente no momento de abertura
  if (grupo.length === 1) {
    const ativar = () => ativarJogoComRetry(grupo[0].api_fixture_id, telaId, schedule, geracaoCapturada);
    if (now >= inicioMs) {
      console.log('[VotacaoAuto] único jogo — ativar imediatamente', label);
      ativar();
    } else {
      console.log('[VotacaoAuto] único jogo — agendar ativação em', Math.round((inicioMs - now) / 60000), 'min', label);
      schedule(ativar, inicioMs - now, geracaoCapturada);
    }
    return;
  }

  // Fluxo normal com votação (2+ jogos)
  const encerrar = () => encerrarCicloComRetry(fixtureIds, telaId, schedule, geracaoCapturada);
  const tentativasAbrir = [...RETRY_BACKOFF_MS];
  const abrir    = async () => {
    console.log('[VotacaoAuto] abrindo ciclo', fixtureIds);
    try {
      await votacaoRepository.iniciarVotacaoCiclo(grupo, telaId);
    } catch (err) {
      // Sem retry, uma falha de rede aqui deixava o ciclo sem abrir e sem
      // encerrar até um reload da TV.
      console.error('[VotacaoAuto] erro ao abrir ciclo', fixtureIds, err);
      if (Date.now() >= fimMs) { encerrar(); return; }
      schedule(abrir, tentativasAbrir.shift() ?? RETRY_PERSISTENTE_MS, geracaoCapturada);
      return;
    }
    const delayEncerramento = fimMs - Date.now();
    console.log('[VotacaoAuto] ciclo aberto', fixtureIds, '— encerramento em', Math.round(delayEncerramento / 60000), 'min');
    schedule(encerrar, delayEncerramento, geracaoCapturada);
  };

  if (now >= fimMs) {
    // Garante que o ciclo foi iniciado antes de encerrar — cobre o caso de
    // Player restartar durante a janela sem ter passado pelo caminho "abrir".
    // iniciarVotacaoCiclo é idempotente: não insere duplicatas.
    console.log('[VotacaoAuto] já passou o fim — garantir abertura e encerrar imediatamente', label);
    votacaoRepository.iniciarVotacaoCiclo(grupo, telaId).then(() => encerrar()).catch(() => encerrar());
  } else if (now >= inicioMs) {
    console.log('[VotacaoAuto] dentro da janela — abrir imediatamente', label);
    abrir();
  } else {
    console.log('[VotacaoAuto] agendado para abrir em', Math.round((inicioMs - now) / 60000), 'min', label);
    schedule(abrir, inicioMs - now, geracaoCapturada);
  }
}

/**
 * Gerencia múltiplos ciclos de votação por dia:
 *  - Um ciclo por grupo de jogos dentro de uma janela de 30 min
 *  - Cada ciclo: abre -30min e encerra -5min do horário do grupo
 *  - Ciclos futuros são agendados mesmo que já exista votação para o ciclo atual
 *  - Fallback Realtime: escuta futebol_jogos_dia se não há jogos no mount
 *
 * Deve ser montado em Player.tsx (24/7).
 */
export function useVotacaoAutomatica(telaId: string): void {
  const cycleTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    if (!telaId) return;

    let destroyed = false;
    // Cada reagendar invalida os anteriores: dois INSERT/DELETE seguidos não
    // duplicam timers, e um fetch que termina depois do cleanup não agenda nada.
    let geracao = 0;

    const scheduleCycle = (fn: () => void, delay: number, geracaoCapturada: number) => {
      if (destroyed || geracaoCapturada !== geracao) return;
      const t = setTimeout(() => {
        if (destroyed || geracaoCapturada !== geracao) return;
        fn();
      }, Math.max(0, delay));
      cycleTimersRef.current.push(t);
    };

    const limparCycleTimers = () => {
      cycleTimersRef.current.forEach(clearTimeout);
      cycleTimersRef.current = [];
    };

    const agendarTodosCiclos = async (minhaGeracao: number): Promise<boolean> => {
      try {
        const todosDia = await votacaoRepository.buscarCandidatosDoDia();
        if (destroyed || minhaGeracao !== geracao) return false;
        console.log('[VotacaoAuto] jogos do dia:', todosDia.map(j => ({ id: j.api_fixture_id, nome: `${j.time_casa} x ${j.time_visitante}`, horario: j.horario, tv: j.televisionado_br })));
        const grupos   = listarGruposDeVotacao(todosDia);
        console.log('[VotacaoAuto] grupos encontrados:', grupos.length);
        if (grupos.length === 0) return false;

        for (const grupo of grupos) {
          if (grupo.every((j) => j.status === 'finalizado')) continue;
          agendarGrupo(grupo, telaId, scheduleCycle, minhaGeracao);
        }
        return true;
      } catch (err) {
        console.error('[VotacaoAuto] erro ao agendar ciclos', err);
        return false;
      }
    };

    const channelRef = { current: null as ReturnType<typeof supabase.channel> | null };

    const reagendar = async () => {
      if (destroyed) return;
      const minhaGeracao = ++geracao;
      limparCycleTimers();
      await agendarTodosCiclos(minhaGeracao);
    };

    let primeiroSubscribe = true;
    void reagendar();

    // Player 24/7: jogos do dia chegam via sync (INSERT/DELETE). Escuta só esses eventos —
    // poll_ativo faz UPDATE a cada minuto e não deve resetar timers de abertura/fechamento.
    channelRef.current = supabase
      .channel(`votacao-automatica-jogos-${telaId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'futebol_jogos_dia' },
        () => { void reagendar(); },
      )
      .on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'futebol_jogos_dia' },
        () => { void reagendar(); },
      )
      // Rejoin do canal: o heartbeat do worker religa o socket, mas não repõe o
      // CDC perdido. Sem reagendar aqui, um INSERT perdido no buraco deixava a
      // TV o dia inteiro sem abrir nem encerrar ciclo de votação.
      .subscribe((status) => {
        if (status !== 'SUBSCRIBED') return;
        if (primeiroSubscribe) { primeiroSubscribe = false; return; }
        void reagendar();
      });

    return () => {
      destroyed = true;
      limparCycleTimers();
      if (channelRef.current) supabase.removeChannel(channelRef.current);
    };
  }, [telaId]);
}
